import { DBJobService, DBVideoService, db } from "@repo/database";
import {
	CreditUsageStatusSchema,
	type Job,
	JobStatusSchema,
	type PredefinedShorts,
	type VideoShortsOptionsWithRelations,
	VideoStatusSchema,
	type VideoWithRelations,
	appErrorsKeys,
} from "@repo/types";
import axios from "axios";
import { intervalToDuration } from "date-fns";
import { getBaseUrl } from "./base-url";
import { configs } from "./configs";
import {
	mapJobForApi,
} from "./general";
import { triggerExternalWebhook } from "./server-helpers";

// Get S3 base URL - use custom domain (CloudFront) if configured, otherwise use standard S3 URL
export const getS3BaseUrl = (): string => {
	if (configs.AWS.customDomain) {
		return `https://${configs.AWS.customDomain}/`;
	}
	const s3Region = process.env.S3_REGION ?? "auto";
	return `https://${configs.AWS.bucketName}.s3.${s3Region}.amazonaws.com/`;
};

const s3baseurl = getS3BaseUrl();
const ratio: number = 9 / 16;

export type videoFileInfo = {
	file: string;
	fps: number;
	dimensions: { videoHeight: number; videoWidth: number };
};

/**
 * @deprecated
 * @param video
 * @param target
 * @returns
 */
export const getVideoPath = (video: VideoWithRelations): string => {
	if (video.files?.isInS3) {
		return s3baseurl + getVideoPathInS3(video, "target");
	}
	return video.files!.target!;
};

/**
 * @deprecated
 * @param video
 * @param target
 * @returns
 */
export const getVideoPathInS3 = (
	video: VideoWithRelations,
	target: "target" | "preview" = "preview",
) => {
	const file = target
		? video.files?.[target]
		: (video.files?.preview ?? video.files?.target);
	if (!file) {
		console.error("file is not found");
	}
	return configs.AWS.mediaPath + file;
};

/**
 * Extracts the S3 key from a URL (supports both S3 direct URLs and CloudFront custom domain URLs)
 * @returns The S3 key extracted from the URL, or the original URL if it doesn't match our bucket
 */
export const getKeyFromUrl = (url: string): string => {
	// Try custom domain first (CloudFront)
	if (configs.AWS.customDomain) {
		const customDomainPrefix = `https://${configs.AWS.customDomain}/`;
		if (url.startsWith(customDomainPrefix)) {
			// Remove the prefix and query params, then trim leading slash if present
			const key = url.replace(customDomainPrefix, "").split("?")[0];
			return key.startsWith("/") ? key.slice(1) : key;
		}
		// Also try without trailing slash (in case URL format is different)
		const customDomainPrefixNoSlash = `https://${configs.AWS.customDomain}`;
		if (url.startsWith(customDomainPrefixNoSlash) && url.length > customDomainPrefixNoSlash.length) {
			// Extract everything after the domain, remove query params, and trim leading slash
			const key = url.slice(customDomainPrefixNoSlash.length).split("?")[0];
			return key.startsWith("/") ? key.slice(1) : key;
		}
	}
	
	// Try standard S3 URL
	const s3Region = process.env.S3_REGION ?? "auto";
	const standardS3Url = `https://${configs.AWS.bucketName}.s3.${s3Region}.amazonaws.com/`;
	if (url.startsWith(standardS3Url)) {
		return url.replace(standardS3Url, "").split("?")[0];
	}
	
	// Try legacy S3 URL format (without region)
	const legacyS3Url = `https://${configs.AWS.bucketName}.s3.amazonaws.com/`;
	if (url.startsWith(legacyS3Url)) {
		return url.replace(legacyS3Url, "").split("?")[0];
	}
	
	// If no match, return original URL (caller should handle this)
	return url;
};

export const generateShortVideoUrlS3Key = (
	videoId: string,
	sourceId: string,
	shortId: number,
	extension: string,
) => {
	const path = `${videoId}-${sourceId}-${shortId}-.${extension}`;
	return configs.AWS.mediaPath + path;
};

export const getShortVideoUrlS3 = (
	videoId: string,
	sourceId: string,
	shortId: number,
	extension: string,
) => {
	// Use getS3UrlFromKey to ensure CloudFront custom domain is used if configured
	return getS3UrlFromKey(
		generateShortVideoUrlS3Key(videoId, sourceId, shortId, extension)
	);
};

export const getS3UrlFromKey = (key: string) => {
	// Use custom domain (CloudFront) if configured, otherwise fall back to standard S3 URL
	if (configs.AWS.customDomain) {
		return `https://${configs.AWS.customDomain}/${key}`;
	}
	const s3Region = process.env.S3_REGION ?? "auto";
	return `https://${configs.AWS.bucketName}.s3.${s3Region}.amazonaws.com/${key}`;
};

export const getVideoFPS = (
	video: VideoWithRelations,
	targetfps: "targetFPS" | "previewFPS",
) => {
	const fileFPS = video.files?.[targetfps];
	if (!fileFPS) {
		console.error("fileFPS is not found");
	}
	return fileFPS!;
};

export const getResolutionFromWidth = (width: number) => {
	return { height: width / ratio, width: width };
};
export const getAvatarsPathInS3 = () => {
	return configs.AWS.avatarsPath;
};

export function getFileExtensionFromMimeType(mimeType: string) {
	const mimeTypes = [
		{ type: "video/mp4", extension: ".mp4" },
		{ type: "video/webm", extension: ".webm" },
		{ type: "video/x-msvideo", extension: ".avi" },
		// Add more mappings as needed
	];

	for (const item of mimeTypes) {
		if (mimeType.startsWith(item.type)) {
			return item.extension;
		}
	}
}

export async function videoShortGenerationIsComplete(
	job: Job,
	video: VideoWithRelations,
) {
	if (video.job.status === JobStatusSchema.Values.COMPLETED) {
		const status = VideoStatusSchema.Values.SUCCESS;
		await DBVideoService.updateVideo(video.id, {
			status,
		});
		await DBJobService.updateJob(video.jobId, {
			status: JobStatusSchema.Values.COMPLETED,
			creditUsageStatus: CreditUsageStatusSchema.Values.ACTIVE,
		});
	}

	await sendFinishWebhookTrigger(job.id);
}

export async function sendFinishWebhookTrigger(jobId: string) {
	const refreshedJobData = await db?.job.findFirst({
		where: {
			id: jobId,
		},
		include: {
			organization: true,
			user: true,
			video: true,
		},
	});
	if (!refreshedJobData?.webhook) {
		console.log(
			"sendWebhookTrigger no job data found, canceling operation",
		);
		return;
	}
	// Type assertion needed until Prisma types are regenerated with render field in costBreakDown
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const data = mapJobForApi(refreshedJobData as any);
	const webhookResult = await triggerExternalWebhook(
		refreshedJobData?.webhook?.url,
		data,
	);
	await DBJobService.updateJob(jobId, {
		webhook: webhookResult,
	});
}

export function getToTalProcessedDuration(
	videoLength: number,
	options?: VideoShortsOptionsWithRelations,
) {
	const isPredefined = options?.predefinedShorts;
	if (isPredefined?.length) {
		const length =
			options?.predefinedShorts?.reduce((price, short) => {
				const duration = short.end - short.start;
				return duration + price;
			}, 0) || 0;
		return length;
	}
	return videoLength;
}

export function videoLengthsValidation(
	videoLength: number,
	options?: VideoShortsOptionsWithRelations,
) {
	const isPredefined = options?.predefinedShorts;
	if (isPredefined?.length) {
		const isValid = isPredefined?.every((short) => {
			const duration = short.end - short.start;
			const minimumDuration: boolean =
				duration >= configs.YTShort.minimumDefinedShortDuration;
			const maximumDuration: boolean =
				duration <= configs.YTShort.maximumDefinedShortDuration;
			return minimumDuration && maximumDuration;
		});

		return isValid
			? true
			: {
					error: appErrorsKeys.invalidPredefinedShortsLength,
				};
	}

	const isMaxDurationExceeded =
		videoLength > configs.YTShort.maxInputDuration;
	return !isMaxDurationExceeded
		? true
		: {
				error: appErrorsKeys.maxVideoLength,
			};
}

export const formatDurationInSeconds = (seconds: number) => {
	const value = intervalToDuration({
		start: new Date(0, 0, 0, 0, 0, 0),
		end: new Date(0, 0, 0, 0, 0, seconds),
	});

	const result : string[] = [];
	if (value.days) value.hours = (value.hours || 0) + value.days * 24;
	if (value.hours) result.push(`${value.hours}h`);
	if (value.minutes) result.push(`${value.minutes}m`);
	if (value.seconds) result.push(`${value.seconds}s`);
	const time = result.join(":");
	return time;
};

export const definedShortDurationToCost = (
	duration: number,
	hourslyPrice: number,
	minimum: number,
) => {
	return durationToCost(duration, hourslyPrice, minimum);
};

export const durationToCost = (
	duration: number,
	hourslyPrice: number,
	minimum?: number,
) => {
	const secondPrice = hourslyPrice / 60 / 60;
	let cost: number = duration * secondPrice;
	cost = Number.parseFloat(cost.toFixed(3));
	if (minimum) {
		cost = cost < minimum ? minimum : cost;
	}
	return cost;
};

// YouTube video quality types
export type YouTubeQuality = "480p" | "720p" | "1080p" | "4K";

// File size estimates per minute by quality (in MB)
const YOUTUBE_FILE_SIZE_PER_MINUTE: Record<YouTubeQuality, number> = {
	"480p": 8,   // ~8MB per minute
	"720p": 15,  // ~15MB per minute
	"1080p": 25, // ~25MB per minute
	"4K": 60,    // ~60MB per minute
} as const;

/**
 * Main centralized cost calculation function for video shorts generation
 * Handles both manual segments and auto-generation pricing
 */
export function getCostFotShortGenFunc(
	hourPrice: number,
	videoDuration?: number,
	predefinedShorts?: PredefinedShorts[],
	segmentMinimumCost?: number,
	durationMinimumCost?: number,
): number {
	if (predefinedShorts?.length) {
		const totalPredefinedShortsPrice = predefinedShorts?.reduce(
			(totalCost, section) => {
				const duration = section.end - section.start;
				const result =
					totalCost + durationToCost(duration, hourPrice, segmentMinimumCost);
				return result;
			},
			0,
		);
		return totalPredefinedShortsPrice;
	}
	if (videoDuration) {
		return durationToCost(videoDuration, hourPrice, durationMinimumCost);
	}
	return 0;
}

/**
 * Unified cost calculation function for short generation
 * This is the recommended function to use for all cost calculations across the app
 * 
 * @param options - Cost calculation options
 * @param options.pricePerProcessedMinute - Price per minute from the active plan
 * @param options.videoDuration - Video duration in seconds (required for auto mode)
 * @param options.predefinedShorts - Array of predefined segments (required for manual mode)
 * @returns Calculated cost in dollars
 * 
 * @example
 * // Auto mode (AI-generated shorts based on full video duration)
 * const cost = calculateShortGenerationCost({
 *   pricePerProcessedMinute: 0.05,
 *   videoDuration: 3600 // 1 hour video
 * });
 * 
 * @example
 * // Manual mode (user-defined segments)
 * const cost = calculateShortGenerationCost({
 *   pricePerProcessedMinute: 0.05,
 *   predefinedShorts: [
 *     { title: "Segment 1", start: 0, end: 60 },
 *     { title: "Segment 2", start: 120, end: 180 }
 *   ]
 * });
 */
export function calculateShortGenerationCost(options: {
	pricePerProcessedMinute: number;
	videoDuration?: number;
	predefinedShorts?: PredefinedShorts[];
}): number {
	const { pricePerProcessedMinute, videoDuration, predefinedShorts } = options;
	
	// Convert price per minute to hourly price
	const hourPrice = pricePerProcessedMinute * 60;
	
	// Determine which mode we're in and use appropriate minimum cost
	const isManualMode = predefinedShorts && predefinedShorts.length > 0;
	
	if (isManualMode) {
		// Manual mode: cost based on predefined segments with segment minimum
		return getCostFotShortGenFunc(
			hourPrice,
			undefined,
			predefinedShorts,
			configs.YTShort.minimumDefinedShortPrice,
			undefined
		);
	}
	
	// Auto mode: cost based on full video duration with duration minimum
	if (videoDuration) {
		return getCostFotShortGenFunc(
			hourPrice,
			videoDuration,
			undefined,
			undefined,
			configs.YTShort.durationMinimumCost
		);
	}
	
	// No valid input provided
	return 0;
}

/**
 * Calculate render cost based on video duration
 * This function calculates the cost of rendering a video using Remotion Lambda
 * 
 * @param options - Cost calculation options
 * @param options.pricePerRenderedMinute - Price per minute of rendered video (from plan or config)
 * @param options.duration - Video duration in seconds (required)
 * @param options.useConfigMinimum - Whether to use config minimum price (default: true)
 * @returns Calculated cost in dollars
 * 
 * @example
 * // Calculate render cost for a 60-second video
 * const cost = calculateRenderCost({
 *   pricePerRenderedMinute: 0.5,
 *   duration: 60
 * });
 * 
 * @example
 * // Calculate render cost with custom minimum
 * const cost = calculateRenderCost({
 *   pricePerRenderedMinute: 0.5,
 *   duration: 10,
 *   useConfigMinimum: true
 * });
 */
export function calculateRenderCost(options: {
	pricePerRenderedMinute: number;
	duration: number;
	useConfigMinimum?: boolean;
}): number {
	const { pricePerRenderedMinute, duration, useConfigMinimum = true } = options;
	
	if (duration <= 0) {
		return 0;
	}
	
	// Convert price per minute to hourly price
	const hourPrice = pricePerRenderedMinute * 60;
	
	// Calculate cost using durationToCost helper
	// Use config minimum if specified, otherwise no minimum
	const minimum = useConfigMinimum ? configs.YTShort.minimumRenderPrice : undefined;
	const cost = durationToCost(duration, hourPrice, minimum);
	
	return cost;
}

/**
 * Calculate YouTube download cost based on file size
 * @param fileSizeBytes - File size in bytes
 * @param pricePerGB - Price per GB (defaults to config value)
 * @returns Cost in dollars
 */
export function calculateYouTubeDownloadCost(
	fileSizeBytes: number,
	pricePerGB: number = configs.YTShort.youtubeDownloadPricePerGB,
): number {
	const fileSizeGB = fileSizeBytes / (1024 * 1024 * 1024); // Convert bytes to GB
	const cost = fileSizeGB * pricePerGB;
	const minimumCost = configs.YTShort.minimumYoutubeProcessingPrice;
	return Number.parseFloat(Math.max(cost, minimumCost).toFixed(4)); // Round to 4 decimal places
}

/**
 * Estimate YouTube download cost based on video duration and quality
 * This is a rough estimation for frontend display before actual download
 * @param durationInSeconds - Video duration in seconds
 * @param quality - Video quality (defaults to 1080p)
 * @param pricePerGB - Price per GB (defaults to config value)
 * @returns Estimated cost in dollars
 */
export function estimateYouTubeDownloadCost(
	durationInSeconds: number,
	quality: YouTubeQuality = "1080p",
	pricePerGB: number = configs.YTShort.youtubeDownloadPricePerGB,
): number {
	const durationInMinutes = durationInSeconds / 60;
	const estimatedSizePerMinute = YOUTUBE_FILE_SIZE_PER_MINUTE[quality];
	const estimatedSizeInMB = durationInMinutes * estimatedSizePerMinute;
	const estimatedSizeInGB = estimatedSizeInMB / 1024; // Convert MB to GB
	
	const estimatedCost = estimatedSizeInGB * pricePerGB;
	const minimumCost = configs.YTShort.minimumYoutubeProcessingPrice;
	return Number.parseFloat(Math.max(estimatedCost, minimumCost).toFixed(4)); // Round to 4 decimal places
}



export const getYoutubeIdFromUrl = (url: string) => {
	const regExp =
		/^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
	const match = url.match(regExp);
	if (match && match[2].length === 11) {
		return match[2];
	}
	return url?.trim();
};

export function getS3KeyFromUrl(url: string): string {
	// First, try to use getKeyFromUrl which handles both S3 and CloudFront URLs
	try {
		const key = getKeyFromUrl(url);
		// If getKeyFromUrl returns a different value, it means it successfully extracted the key
		if (key !== url) {
			return key;
		}
	} catch (error) {
		// Fall through to legacy parsing if getKeyFromUrl doesn't work
	}

	// Legacy parsing for standard S3 URLs (fallback)
	// Remove the protocol prefix if it exists
	const withoutProtocol = url.replace(/^https?:\/\//, "");
	
	// Check if it's a custom domain (CloudFront) URL
	if (configs.AWS.customDomain && withoutProtocol.startsWith(configs.AWS.customDomain)) {
		return withoutProtocol.replace(`${configs.AWS.customDomain}/`, "").split("?")[0];
	}
	
	// Split by the first occurrence of '.s3.' to get the part after the bucket name
	const parts = withoutProtocol.split(".s3.");

	if (parts.length < 2) {
		throw new Error("Invalid S3 URL format");
	}
	// Split the second part by '/' and remove the first empty element (from the leading '/')
	const pathParts = parts[1].split("/").slice(1);
	// Join the remaining parts to form the key
	return pathParts.join("/");
}

/**
 * Trim video via VPS service
 * This function sends a trim request to an external VPS service.
 * It will retry up to 3 times (2 additional retries) before failing.
 * If neither service is configured or the request fails after all retries, it should be used as a fallback to renderAndProcessShortRemotion.
 * 
 * @param video - The video with relations
 * @param shortId - The ID of the short to trim
 * @returns Promise that resolves with the service response containing status, jobId, and message
 * @throws Error if neither service is configured or the request fails after all retries
 */
export async function TrimVideoShort(
	video: VideoWithRelations,
	shortId: number
): Promise<{
    "status": "queued" | "processing" | "completed" | "failed",
    "jobId": string,
    "message": string,
}> {
	const isYoutubeVideo = !!video.sourceId;

	// Try VPS service first, then Modal service
	const modalServiceUrl = isYoutubeVideo ? configs.modal.youtubeTrimUrl : configs.modal.trimServiceUrl;
	
	// Construct dynamically using base URL + webhook path
	const baseUrl = getBaseUrl();
	const webhookBaseUrl = `${baseUrl}/api/webhooks/n8n/trimShort/complete`;
	console.log(`[TrimVideoShort] - Constructed webhook URL dynamically: ${webhookBaseUrl}`);


	// Validate video and short
	if (!video.shorts?.length) {
		throw new Error(
			`[TrimVideoShort] - Video or video file target not found for video: ${video.id}`,
		);
	}

	const short = video.shorts.find((s) => s.id === shortId);
	if (!short) {
		throw new Error(
			`[TrimVideoShort] - Short not found for shortId: ${shortId} in video: ${video.id}`,
		);
	}

	// Build webhook URL with query parameters
	const webhookUrl = `${webhookBaseUrl}?videoId=${video.id}&shortId=${shortId}`;

	const startTime = short.startTime;
	const endTime = short.endTime;
	// Prepare request payload
	const payload = isYoutubeVideo ? {
		url: `https://www.youtube.com/watch?v=${video.sourceId}`,
		format: "bestvideo[height<=1080][vcodec*=avc1]+bestaudio[acodec*=mp4a]/best",
		start_time: startTime,
		end_time: endTime,
		webhookUrl,
	  } : {
		videoUrl: video.files?.target,
		options: {
			startTime,
			endTime,
		},
		webhookUrl,
	};

	const maxRetries = 3;
	
	for (let attempt = 1; attempt <= maxRetries; attempt++) {
		try {
			console.log(
				`[TrimVideoShort] - Attempt ${attempt}/${maxRetries} - isYoutube: ${isYoutubeVideo} - Sending trim request to service for video: ${video.id}, short: ${shortId}`,
			);
			if (attempt === 1) {
				console.log(`[TrimVideoShort] - ${modalServiceUrl} Service URL: ${modalServiceUrl}`);
				console.log(`[TrimVideoShort] - Webhook URL: ${webhookUrl}`);
				console.log("[TrimVideoShort] - Payload:", JSON.stringify(payload, null, 2));
			}

			const response = await axios.post(modalServiceUrl, payload, {
				headers: {
					"Content-Type": "application/json",
				},
				timeout: 30000, // 30 seconds timeout
			});

			if (response.status >= 200 && response.status < 300) {
				console.log(
					`[TrimVideoShort] - Successfully sent trim request to ${modalServiceUrl} service. Status: ${response.status}`,
				);
				return response.data;
			}
			throw new Error(
				`[TrimVideoShort] - ${modalServiceUrl} service returned non-success status: ${response.status}`,
			);
		} catch (error) {
			console.error(
				`[TrimVideoShort] - Attempt ${attempt}/${maxRetries} failed:`,
				error instanceof Error ? error.message : error,
			);
			
			if (attempt === maxRetries) {
				console.error(
					`[TrimVideoShort] - Max retries reached. Failing for video: ${video.id}, short: ${shortId}`,
				);
				throw new Error(
					`[TrimVideoShort] - Failed to send trim request to ${modalServiceUrl} service after ${maxRetries} attempts: ${
						error instanceof Error ? error.message : "Unknown error"
					}`,
				);
			}
			
			// Wait before retrying (exponential backoff)
			const delay = 1000 * attempt; // 1s, 2s, 3s...
			console.log(`[TrimVideoShort] - Retrying in ${delay}ms...`);
			await new Promise((resolve) => setTimeout(resolve, delay));
		}
	}
	
	// This should never be reached, but TypeScript requires it
	throw new Error(
		"[TrimVideoShort] - Unexpected error: Loop completed without returning or throwing",
	);
}